import { Controller } from "stimulus";
import {
  childrenList,
  index,
  setIndex,
  depth,
  setDepth,
  RulesEngine,
} from "../utils/rules-engine";

const objectType = "navigation";

function item(event) {
  return event.target.closest("[data-navigation-item]");
}

/**
 * Returns navigation nodes that are visible and logically nested below the given node.
 *
 * @param node
 * @returns {Element[]}
 */
function visibleDescendants(node) {
  const descendants = [];

  for (
    let sibling = node.nextElementSibling;
    sibling && depth(sibling, objectType) > depth(node, objectType);
    sibling = sibling.nextElementSibling
  ) {
    descendants.push(sibling);
  }

  return descendants;
}

/**
 * Returns navigation nodes that have been collapsed into the given node.
 *
 * @param node
 * @returns {Element[]}
 */
function hiddenDescendants(node) {
  return Array.from(childrenList(node, objectType).children);
}

function setOpen(node, open = true) {
  node.querySelector("[data-navigation-open-input]").value = open ? 1 : 0;
}

function setDestroy(node, destroy = true) {
  node.dataset.destroy = destroy;
  node.querySelector("[data-navigation-destroy-input]").value = destroy ? 1 : 0;
}

function blur(event) {
  event.target.closest("button").blur();
}

export default class extends Controller {
  static targets = ["menu"];

  connect() {
    this.reindex();
    this.focusFirstInput();
  }

  reindex() {
    this.navItems().map((node, index) => setIndex(node, index, objectType));
    this.updateNavItems();
  }

  focusFirstInput() {
    let openNodes = this.navItems().filter((e) =>
      Array.from(e.classList).includes("open"),
    );

    if (Array.isArray(openNodes) && openNodes.length) {
      let firstInput = openNodes[0].querySelector(
        "input:not([type=hidden]), select:not([type=hidden]), textarea:not([type=hidden])",
      );

      firstInput.focus();
    }
  }

  reset() {
    this.navItems()
      .sort(compare)
      .forEach((node) => this.menuTarget.appendChild(node));
  }

  navItems() {
    return Array.from(
      this.menuTarget.querySelectorAll("[data-navigation-index]"),
    );
  }

  updateNavItems() {
    setTimeout(() => new RulesEngine(objectType).update(this.navItems()), 0);
  }

  open(event) {
    setOpen(item(event));
  }

  close(event) {
    setOpen(item(event), false);
  }

  remove(event) {
    const node = item(event);
    const shouldDestroy = node.dataset.destroy === "false";

    setDestroy(node, shouldDestroy);

    hiddenDescendants(node).forEach((node) => setDestroy(node, shouldDestroy));

    blur(event);
    event.preventDefault();
  }

  nest(event) {
    const node = item(event);

    setDepth(node, depth(node, objectType) + 1, objectType);

    hiddenDescendants(node).forEach((node) =>
      setDepth(node, depth(node, objectType) + 1, objectType),
    );

    this.updateNavItems();

    blur(event);
    event.preventDefault();
  }

  deNest(event) {
    const node = item(event);

    setDepth(node, depth(node, objectType) - 1, objectType);

    hiddenDescendants(node).forEach((node) =>
      setDepth(node, depth(node, objectType) - 1, objectType),
    );

    this.updateNavItems();

    blur(event);
    event.preventDefault();
  }

  collapse(event) {
    const node = item(event);
    const children = childrenList(node, objectType);

    visibleDescendants(node).forEach((e) => children.appendChild(e));

    this.updateNavItems();

    blur(event);
    event.preventDefault();
  }

  expand(event) {
    const node = item(event);

    hiddenDescendants(node)
      .reverse()
      .forEach((item) => node.insertAdjacentElement("afterend", item));

    this.updateNavItems();

    blur(event);
    event.preventDefault();
  }
}
